/**
 * Rendez-vous CIBLÉS (express/entretien) — créés directement par le tuteur
 * pour UN élève choisi, sans passer par le pipeline règle hebdomadaire→créneau
 * matérialisé (cours/étude). RPC `create_direct_rendezvous` (migration 0237),
 * postérieure aux types générés → client générique (calque
 * `apps/app/src/lib/tutor/booking.ts`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { RendezVousKind } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { sendNotification } from '@/lib/notifications/send-notification';
import { extractInvokeErrorCode, translateSummaryError } from '@/lib/visio/summary-errors';
import { fetchVisioRoomId } from '@/lib/visio/room-lookup';

const db = supabase as unknown as SupabaseClient;

export type DirectRendezVousKind = 'express' | 'entretien';

export interface CreateDirectRendezVousInput {
  kind: DirectRendezVousKind;
  studentId: string;
  startsAt: Date;
  durationMinutes: number;
  /** Série de points choisie dès la création (migration 0248) — proposée par défaut dans la visio et le résumé. */
  seriesId?: string | null;
}

export interface CreateDirectRendezVousResult {
  reservationId: string;
  slotId: string;
  startsAt: string;
  endsAt: string;
}

/** Crée un rendez-vous ciblé (express/entretien) et renvoie son reservation_id — exploitable tel quel par useJoinVisioRoom. */
export function useCreateDirectRendezVous() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: CreateDirectRendezVousInput,
    ): Promise<CreateDirectRendezVousResult> => {
      const { data, error } = await db
        .rpc('create_direct_rendezvous', {
          p_kind: input.kind,
          p_student_id: input.studentId,
          p_starts_at: input.startsAt.toISOString(),
          p_duration_minutes: input.durationMinutes,
        })
        .single();
      if (error) throw new Error(error.message);
      const row = data as {
        reservation_id: string;
        slot_id: string;
        starts_at: string;
        ends_at: string;
      };
      if (input.seriesId) {
        // Best-effort : le rendez-vous EXISTE déjà — un échec ici ne doit pas faire croire qu'il n'a pas été créé.
        const { error: choiceError } = await db
          .from('rendezvous_series_choices')
          .upsert(
            { reservation_id: row.reservation_id, series_id: input.seriesId },
            { onConflict: 'reservation_id' },
          );
        if (choiceError) console.error('[rendezvous] série non enregistrée', choiceError);
      }
      return {
        reservationId: row.reservation_id,
        slotId: row.slot_id,
        startsAt: row.starts_at,
        endsAt: row.ends_at,
      };
    },
    onSuccess: (result, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-booking-slots'] });
      void sendNotification({
        type: 'rendezvous_scheduled',
        student_id: variables.studentId,
        reservation_id: result.reservationId,
      });
    },
  });
}

// ── Mentorat académique (entretiens alternance) ───────────────────────────────

export interface AcademicMentee {
  studentId: string;
  displayName: string;
  /**
   * true = alternant DÉCLARÉ par l'étudiant (onboarding « Étudiant »), false =
   * élève basculé par son tuteur (migration 0250) — décide du libellé/effet du retrait.
   */
  isHigherEd: boolean;
}

/** Étudiants alternants ayant CE tuteur comme tuteur académique — alimente le sélecteur "entretien". */
export function useAcademicMentees(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['academic-mentees', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<AcademicMentee[]> => {
      // ⚠️ student_profiles a 2 FK vers profiles depuis la migration 0237
      // (id_fkey + academic_tutor_id_fkey) → désambiguïser l'embed, comme
      // tutor_links/video_rooms (cf. CLAUDE.md).
      const { data, error } = await db
        .from('student_profiles')
        .select(
          'id, is_higher_ed_student, profiles!student_profiles_id_fkey(display_name:visible_name)',
        )
        .eq('academic_tutor_id', tutorId!)
        .eq('is_alternant', true);
      if (error) throw new Error(error.message);
      type Row = {
        id: string;
        is_higher_ed_student: boolean | null;
        profiles: { display_name: string } | null;
      };
      return ((data ?? []) as unknown as Row[]).map((row) => ({
        studentId: row.id,
        displayName: row.profiles?.display_name ?? 'Étudiant',
        isHigherEd: Boolean(row.is_higher_ed_student),
      }));
    },
  });
}

export interface AcademicMentor {
  tutorId: string;
  displayName: string;
  bio: string | null;
}

/** Tuteurs volontaires (offers_academic_mentoring=true) — sélecteur élève "Choisir mon tuteur académique". */
export function useAcademicMentors(query: string) {
  return useQuery({
    queryKey: ['academic-mentors', query],
    queryFn: async (): Promise<AcademicMentor[]> => {
      const { data, error } = await db.rpc('list_academic_mentors', {
        p_query: query || null,
        p_limit: 20,
      });
      if (error) throw new Error(error.message);
      type Row = { tutor_id: string; display_name: string; bio: string | null };
      return ((data ?? []) as Row[]).map((row) => ({
        tutorId: row.tutor_id,
        displayName: row.display_name,
        bio: row.bio,
      }));
    },
  });
}

/** L'étudiant choisit son tuteur académique (exige is_alternant=true côté trigger DB). */
export function useSetAcademicTutor(studentId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tutorId: string | null) => {
      if (!studentId) throw new Error('missing studentId');
      const { error } = await db
        .from('student_profiles')
        .update({ academic_tutor_id: tutorId })
        .eq('id', studentId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-higher-ed-profile', studentId] }),
  });
}

/** Toggle tuteur : propose (ou non) du mentorat académique gratuit. */
export function useSetOffersAcademicMentoring(tutorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!tutorId) throw new Error('missing tutorId');
      const { error } = await db
        .from('tutor_profiles')
        .update({ offers_academic_mentoring: enabled })
        .eq('id', tutorId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offers-academic-mentoring', tutorId] }),
  });
}

// ── Détail d'un rendez-vous (cible du lien d'invitation partageable) ─────────

export interface RendezVousDetail {
  reservationId: string;
  status: string;
  kind: RendezVousKind;
  startsAt: string;
  endsAt: string;
  /** Fin RÉELLE (le tuteur a terminé la réunion, migration 0249) ; `null` = jamais terminé explicitement. */
  endedAt: string | null;
  tutorId: string;
  tutorName: string;
  studentId: string | null;
  studentName: string | null;
}

/**
 * Détail d'UN rendez-vous par son reservation_id — cible de `/rendez-vous/$reservationId`
 * (lien d'invitation partagé) ; accessible tuteur OU élève (RLS own_select/tutor_select).
 */
export function useRendezVousDetail(reservationId: string | undefined) {
  return useQuery({
    queryKey: ['rendezvous-detail', reservationId],
    enabled: !!reservationId,
    queryFn: async (): Promise<RendezVousDetail | null> => {
      const { data, error } = await db
        .from('tutor_booking_reservations')
        .select(
          'id, status, ended_at, student_id, profiles!tutor_booking_reservations_student_id_fkey(display_name:visible_name), tutor_booking_slots(kind, starts_at, ends_at, tutor_id, profiles(display_name:visible_name))',
        )
        .eq('id', reservationId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      type Row = {
        id: string;
        status: string;
        ended_at: string | null;
        student_id: string | null;
        profiles: { display_name: string } | null;
        tutor_booking_slots: {
          kind: RendezVousKind;
          starts_at: string;
          ends_at: string;
          tutor_id: string;
          profiles: { display_name: string } | null;
        } | null;
      };
      const row = data as unknown as Row;
      if (!row.tutor_booking_slots) return null;
      return {
        reservationId: row.id,
        status: row.status,
        kind: row.tutor_booking_slots.kind,
        startsAt: row.tutor_booking_slots.starts_at,
        endsAt: row.tutor_booking_slots.ends_at,
        endedAt: row.ended_at,
        tutorId: row.tutor_booking_slots.tutor_id,
        tutorName: row.tutor_booking_slots.profiles?.display_name ?? 'Ton prof',
        studentId: row.student_id,
        studentName: row.profiles?.display_name ?? null,
      };
    },
  });
}

// ── Dialogue du chat + résumé d'un rendez-vous passé ─────────────────────────

export interface RendezVousMessage {
  id: string;
  senderId: string;
  body: string;
  /** 'voice' = phrase retranscrite en live ; 'file' = fichier partagé (`body` = son nom, migration 0262) ; 'typed' = message tapé. */
  source: 'typed' | 'voice' | 'file';
  /** Fichier partagé (`source='file'`). `null` sur un message-fichier = fichier supprimé depuis. */
  documentId: string | null;
  createdAt: string;
  /** Non null = message MASQUÉ dans la visio (suppression douce, migration 0246) : exclu du résumé. */
  hiddenAt: string | null;
}

/** Nom + avatar d'un participant, par id (RPC `get_visio_room_participants`). */
export interface RendezVousPerson {
  displayName: string;
  avatarUrl: string | null;
}

export interface RendezVousDialogueData {
  messages: RendezVousMessage[];
  people: Record<string, RendezVousPerson>;
}

/**
 * Dialogue du chat d'un rendez-vous (messages tapés + phrases retranscrites) et
 * identité des deux participants (nom + avatar). Résout `video_rooms` depuis
 * `reservation_id`, puis lit `visio_room_messages` — RLS participants_select :
 * tuteur ET élève voient le dialogue auquel ils ont pris part, MASQUÉS compris
 * (le tri visible/masqué se fait à l'affichage : seul le tuteur voit les
 * masqués, pour les rétablir). Les avatars passent par une RPC (la RLS de
 * `profiles` ne couvre pas tous les couples élève/tuteur) ; son échec n'empêche
 * pas d'afficher le dialogue. Liste vide si la salle n'a jamais été ouverte.
 */
export function useRendezVousDialogue(reservationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['rendezvous-dialogue', reservationId],
    enabled: !!reservationId && enabled,
    queryFn: async (): Promise<RendezVousDialogueData> => {
      // Une salle par CRÉNEAU (0255) : on la retrouve par le créneau de la réservation.
      const roomId = await fetchVisioRoomId(db, reservationId!);
      if (!roomId) return { messages: [], people: {} };

      const [messagesRes, peopleRes] = await Promise.all([
        db
          .from('visio_room_messages')
          .select('id, sender_id, body, source, created_at, hidden_at, document_id')
          .eq('room_id', roomId)
          .order('created_at', { ascending: true }),
        db.rpc('get_visio_room_participants', { p_room_id: roomId }),
      ]);
      if (messagesRes.error) throw new Error(messagesRes.error.message);

      type Row = {
        id: string;
        sender_id: string;
        body: string;
        source: 'typed' | 'voice' | 'file';
        created_at: string;
        hidden_at: string | null;
        document_id: string | null;
      };
      type PersonRow = { user_id: string; display_name: string | null; avatar_url: string | null };
      const people: Record<string, RendezVousPerson> = {};
      for (const row of (peopleRes.error ? [] : (peopleRes.data ?? [])) as PersonRow[]) {
        people[row.user_id] = { displayName: row.display_name ?? '', avatarUrl: row.avatar_url };
      }
      return {
        messages: ((messagesRes.data ?? []) as Row[]).map((row) => ({
          id: row.id,
          senderId: row.sender_id,
          body: row.body,
          source: row.source,
          documentId: row.document_id ?? null,
          createdAt: row.created_at,
          hiddenAt: row.hidden_at,
        })),
        people,
      };
    },
  });
}

/**
 * Masque (`hidden=true`) ou rétablit un message du chat — suppression douce,
 * RPC `set_visio_room_message_hidden` (droits : l'auteur ou le tuteur ; le
 * serveur reste juge). Un message masqué n'entre pas dans le résumé.
 */
export function useSetRendezVousMessageHidden(reservationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; hidden: boolean }) => {
      const { error } = await db.rpc('set_visio_room_message_hidden', {
        p_message_id: input.messageId,
        p_hidden: input.hidden,
      });
      if (error) throw new Error(error.message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['rendezvous-dialogue', reservationId] }),
  });
}

export interface RendezVousSummary {
  /** Markdown simple, à rendre via `SummaryMarkdown` (jamais en HTML brut). */
  summaryMd: string;
  /** Instructions utilisées pour ce résumé (NULL pour une ligne antérieure à la migration 0245). */
  promptMd: string | null;
  /** Série de points utilisée (migration 0247) : id (NULL si supprimée depuis) et titre (instantané). */
  seriesId: string | null;
  seriesTitle: string | null;
  generatedAt: string;
}

/**
 * Résumé déjà généré d'un rendez-vous (`visio_room_notes`, migration 0245) —
 * lisible du TUTEUR de la salle seul (RLS tutor_select).
 */
export function useRendezVousSummary(reservationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['rendezvous-summary', reservationId],
    enabled: !!reservationId && enabled,
    queryFn: async (): Promise<RendezVousSummary | null> => {
      const roomId = await fetchVisioRoomId(db, reservationId!);
      if (!roomId) return null;

      const { data, error } = await db
        .from('visio_room_notes')
        .select('summary, prompt_md, series_id, series_title, generated_at')
        .eq('room_id', roomId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const row = data as {
        summary: { summary_md?: unknown } | null;
        prompt_md: string | null;
        series_id: string | null;
        series_title: string | null;
        generated_at: string;
      };
      if (typeof row.summary?.summary_md !== 'string') return null;
      return {
        summaryMd: row.summary.summary_md,
        promptMd: row.prompt_md,
        seriesId: row.series_id,
        seriesTitle: row.series_title,
        generatedAt: row.generated_at,
      };
    },
  });
}

/**
 * Génère (ou régénère) le résumé selon les instructions du tuteur — edge fn
 * `visio-room-summarize` (le serveur relit le dialogue et reste seul juge de
 * l'autorisation). Le résultat remplace le résumé en cache.
 */
export function useGenerateRendezVousSummary(reservationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      /** Instructions bâties depuis la série (`buildSeriesInstructions`). */
      instructions: string;
      /** Série dont elles sont issues — enregistrée avec le résumé. */
      seriesId: string | null;
    }): Promise<RendezVousSummary> => {
      const { instructions, seriesId } = input;
      const { data, error } = await supabase.functions.invoke('visio-room-summarize', {
        body: {
          reservation_id: reservationId,
          instructions,
          ...(seriesId ? { series_id: seriesId } : {}),
        },
      });
      if (error) throw new Error(translateSummaryError(await extractInvokeErrorCode(error)));
      const result = data as {
        summary_md?: string;
        prompt_md?: string;
        series_id?: string | null;
        series_title?: string | null;
        generated_at?: string;
      } | null;
      if (!result?.summary_md) throw new Error(translateSummaryError(undefined));
      return {
        summaryMd: result.summary_md,
        promptMd: result.prompt_md ?? instructions,
        seriesId: result.series_id ?? null,
        seriesTitle: result.series_title ?? null,
        generatedAt: result.generated_at ?? new Date().toISOString(),
      };
    },
    onSuccess: (summary) => qc.setQueryData(['rendezvous-summary', reservationId], summary),
  });
}

export function useOffersAcademicMentoring(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['offers-academic-mentoring', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await db
        .from('tutor_profiles')
        .select('offers_academic_mentoring')
        .eq('id', tutorId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return Boolean(
        (data as { offers_academic_mentoring: boolean } | null)?.offers_academic_mentoring,
      );
    },
  });
}
